import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true,
});

export async function extractCases(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/extract", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
}

export async function calculateLoad(cases, vehicles, options = {}) {
  const { data } = await api.post("/calculate", {
    cases, vehicles,
    optimize_min_trucks: !!options.optimizeMinTrucks,
  });
  return data;
}

export async function getDefaultVehicles() {
  const { data } = await api.get("/vehicles/defaults");
  return data;
}

export async function getExportDefaultVehicles() {
  const { data } = await api.get("/vehicles/export-defaults");
  return data;
}

export async function saveCalculation(record) {
  const { data } = await api.post("/calculations", record);
  return data;
}

export async function listCalculations() {
  const { data } = await api.get("/calculations");
  return data;
}

export async function deleteCalculation(id) {
  const { data } = await api.delete(`/calculations/${id}`);
  return data;
}

// ---------- Templates ----------
export async function saveTemplate(record) {
  const { data } = await api.post("/templates", record);
  return data;
}

export async function listTemplates() {
  const { data } = await api.get("/templates");
  return data;
}

export async function deleteTemplate(id) {
  const { data } = await api.delete(`/templates/${id}`);
  return data;
}

// ---------- Reports ----------
export async function createReport({ title, description, contact, file }) {
  const fd = new FormData();
  fd.append("title", title || "");
  fd.append("description", description || "");
  fd.append("contact", contact || "");
  if (file) fd.append("file", file);
  const { data } = await api.post("/reports", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return data;
}

export async function listReports(status = null) {
  const params = status ? { status } : {};
  const { data } = await api.get("/reports", { params });
  return data;
}

export async function updateReport(id, patch, adminPin) {
  const headers = adminPin ? { "X-Admin-Pin": adminPin } : {};
  const { data } = await api.patch(`/reports/${id}`, patch, { headers });
  return data;
}

export async function deleteReport(id, adminPin) {
  const headers = adminPin ? { "X-Admin-Pin": adminPin } : {};
  const { data } = await api.delete(`/reports/${id}`, { headers });
  return data;
}

export function reportAttachmentUrl(id) {
  return `${API}/reports/${id}/attachment`;
}

// ---------- Admin PIN ----------
export async function changeAdminPin(currentPin, newPin) {
  const { data } = await api.post(
    "/admin/pin/change",
    { new_pin: newPin },
    { headers: { "X-Admin-Pin": currentPin } },
  );
  return data;
}

export async function downloadExcel(payload) {
  return _downloadBlob("/export/excel", payload, "cargo_load_plan.xlsx");
}

export async function downloadPdf(payload) {
  return _downloadBlob("/export/pdf", payload, "cargo_load_plan.pdf");
}

async function _downloadBlob(path, payload, fallbackName) {
  const res = await api.post(path, payload, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="(.+?)"/);
  const filename = match ? match[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}


// ---------- Shared Plans (public read-only URL) ----------
export async function createSharedPlan({ name, trip, brand, cases, vehicles, result }) {
  const { data } = await api.post("/share", { name, trip, brand, cases, vehicles, result });
  return data;
}

export async function getSharedPlan(token) {
  const { data } = await api.get(`/share/${token}`);
  return data;
}

export function sharedPlanUrl(token) {
  return `${window.location.origin}/plan/${token}`;
}


// ---------- Auth-scoped brand ----------
export async function fetchUserBrand() {
  const { data } = await api.get("/user/brand");
  return data;
}

export async function saveUserBrand(brand) {
  const { data } = await api.put("/user/brand", brand);
  return data;
}

// ---------- Password reset ----------
export async function requestPasswordReset(email) {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
}

export async function submitPasswordReset(token, newPassword) {
  const { data } = await api.post("/auth/reset-password", {
    token,
    new_password: newPassword,
  });
  return data;
}

// ---------- Phone-based password reset (Firebase OTP) ----------
export async function resetPasswordByPhone(phone, idToken, newPassword) {
  const { data } = await api.post("/auth/reset-by-phone", {
    phone,
    id_token: idToken,
    new_password: newPassword,
  });
  return data;
}

export async function saveVerifiedPhone(phone, idToken) {
  const { data } = await api.put(
    "/user/phone",
    { phone },
    { headers: { "X-Firebase-Id-Token": idToken } },
  );
  return data;
}


export function formatINR(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  const neg = n < 0;
  n = Math.abs(Math.round(n));
  const s = String(n);
  if (s.length <= 3) return (neg ? "-" : "") + s;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const groups = [];
  while (rest.length > 2) {
    groups.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) groups.unshift(rest);
  return (neg ? "-" : "") + groups.join(",") + "," + last3;
}
